import { CSSProperties } from 'react'
import classNames from 'classnames'
import Typography, {
  TypographyTag,
  TypographyVariant,
} from '~/components/Typography'
import {
  getProductPriceBadge,
  isShowEverydayPrice,
  ProductPriceDiscountType,
  resolveProductPriceDisplay,
  splitFormattedPrice,
} from '~/helpers'
import { useFormatPrice } from '~/hooks'
import styles from './ProductPrice.module.scss'
import { ProductPriceProps } from './ProductPrice.types'

const BADGE_CLASS_NAMES: Record<ProductPriceDiscountType, string> = {
  [ProductPriceDiscountType.Promo]: styles.badgePromo,
  [ProductPriceDiscountType.Member]: styles.badgeMember,
  [ProductPriceDiscountType.Trade]: styles.badgeTrade,
  [ProductPriceDiscountType.Staff]: styles.badgeStaff,
}

export const ProductPrice = ({
  finalPrice,
  regularPrice,
  memberPrice,
  hasPromoDiscount = false,
  hasCustomerTypeDiscount = false,
  resolvedCustomerType,
  tag,
  className,
}: ProductPriceProps) => {
  const { formatPrice } = useFormatPrice()

  // A tagged product is excluded from member pricing and shows the tag beside its final price
  const isTagPrice = !!tag?.desc
  const display = isTagPrice
    ? {
        finalPrice,
        regularPrice,
        hasPromoDiscount,
        hasCustomerTypeDiscount,
        resolvedCustomerType,
      }
    : resolveProductPriceDisplay({
        finalPrice,
        regularPrice,
        memberPrice,
        hasPromoDiscount,
        hasCustomerTypeDiscount,
        resolvedCustomerType,
      })
  const badge: { label: string; type?: ProductPriceDiscountType } = isTagPrice
    ? { label: tag.desc }
    : getProductPriceBadge(display)
  const badgeClassName = isTagPrice
    ? styles.badgeTag
    : badge.type && BADGE_CLASS_NAMES[badge.type]
  const badgeStyle: CSSProperties | undefined = isTagPrice
    ? ({
        '--product-price-tag-background': tag.backgroundColor,
        '--product-price-tag-color': tag.color,
      } as CSSProperties)
    : undefined
  // A tagged product shows the everyday line whenever its final price differs from the regular price
  const isShowEverydayPriceRow = isTagPrice
    ? !!regularPrice?.centAmount &&
      !!finalPrice?.centAmount &&
      regularPrice.centAmount !== finalPrice.centAmount
    : isShowEverydayPrice(display)

  const {
    symbol: finalPriceSymbol,
    dollars: finalPriceDollars,
    cents: finalPriceCents,
  } = splitFormattedPrice(
    display.finalPrice ? formatPrice(display.finalPrice) : ''
  )

  const {
    symbol: regularPriceSymbol,
    dollars: regularPriceDollars,
    cents: regularPriceCents,
  } = splitFormattedPrice(regularPrice ? formatPrice(regularPrice) : '')

  if (!display.finalPrice) {
    return null
  }

  return (
    <div className={classNames(styles.container, className)}>
      <div className={styles.priceRow}>
        <Typography
          tag={TypographyTag.span}
          variant={TypographyVariant.BodyLargeBold}
          className={styles.finalPrice}>
          <span className={styles.finalPriceSymbol}>{finalPriceSymbol}</span>
          {finalPriceDollars}
          {finalPriceCents && (
            <span className={styles.finalPriceCents}>.{finalPriceCents}</span>
          )}
        </Typography>

        {badge.label && (
          <Typography
            tag={TypographyTag.span}
            variant={TypographyVariant.BodyRegular}
            className={classNames(styles.badge, badgeClassName)}
            style={badgeStyle}>
            {badge.label}
          </Typography>
        )}
      </div>

      {isShowEverydayPriceRow && (
        <div className={styles.everydayPriceRow}>
          <Typography
            tag={TypographyTag.del}
            variant={TypographyVariant.BodyRegular}>
            {regularPriceSymbol}
            {regularPriceDollars}
            {regularPriceCents && `.${regularPriceCents}`}
          </Typography>
          <Typography
            tag={TypographyTag.span}
            variant={TypographyVariant.BodyRegular}>
            Everyday Price
          </Typography>
        </div>
      )}
    </div>
  )
}
